import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, Paperclip, FileText, FileSpreadsheet, FileImage, FileArchive, Download, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { hasRole } from "@/lib/roles";
import { plural } from "@/lib/plural";

const ICONS_BY_EXT = {
  pdf: FileText, doc: FileText, docx: FileText, txt: FileText,
  csv: FileSpreadsheet, xls: FileSpreadsheet, xlsx: FileSpreadsheet,
  png: FileImage, jpg: FileImage, jpeg: FileImage,
  zip: FileArchive,
};

const ext = (name) => (name?.includes(".") ? name.split(".").pop().toLowerCase() : "");
const humanSize = (b) => {
  if (b == null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};
const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};

/**
 * Right-side panel listing every evidence file uploaded across all indicators of
 * the assessment. Polls /api/assessments/{id}/evidence every 5 s + listens to a
 * window CustomEvent `evidence:updated` for instant refresh after upload/delete.
 */
export function EvidencePanel({ assessmentId }) {
  const { user } = useAuth();
  const canWrite = hasRole(user, "valuatore", "cliente");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assessments/${assessmentId}/evidence`);
      setFiles(data || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    const handler = () => load();
    window.addEventListener("evidence:updated", handler);
    return () => {
      clearInterval(i);
      window.removeEventListener("evidence:updated", handler);
    };
  }, [load]);

  async function download(f) {
    try {
      const res = await api.get(`/evidence/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url; link.download = f.filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Errore download");
    }
  }

  async function remove(f) {
    if (!window.confirm(`Eliminare "${f.filename}"?`)) return;
    try {
      await api.delete(`/evidence/${f.id}`);
      toast.success("File eliminato");
      window.dispatchEvent(new CustomEvent("evidence:updated"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore eliminazione");
    }
  }

  const byIndicator = useMemo(() => {
    const map = {};
    files.forEach((f) => { (map[f.indicator_code] ||= []).push(f); });
    return map;
  }, [files]);

  const indicatorCodes = useMemo(
    () => Object.keys(byIndicator).sort(), [byIndicator]
  );

  return (
    <aside
      className="w-64 shrink-0 border-l border-slate-200 bg-sky-50/40 flex flex-col h-full"
      data-testid="evidence-panel"
    >
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-viva-600" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm text-slate-900">Evidenze caricate</div>
          <div className="text-[11px] text-slate-500">
            {loading ? "Caricamento…" : `${plural(files.length, "file", "file")} · ${plural(indicatorCodes.length, "indicatore", "indicatori")}`}
          </div>
        </div>
        <button
          onClick={load}
          title="Aggiorna"
          className="p-1 rounded text-slate-400 hover:text-viva-600 hover:bg-viva-50"
          data-testid="evidence-panel-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {loading && files.length === 0 ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-viva-400" />
          </div>
        ) : files.length === 0 ? (
          <div className="text-center text-xs text-slate-400 italic py-12 px-4">
            Nessuna evidenza caricata. Carica i file usando le aree
            <br />sotto ogni indicatore.
          </div>
        ) : (
          <ul className="space-y-3">
            {indicatorCodes.map((code) => (
              <li key={code} data-testid={`evidence-panel-group-${code}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 px-1 mb-1">
                  {code}
                </div>
                <ul className="space-y-1">
                  {byIndicator[code].map((f) => {
                    const Icon = ICONS_BY_EXT[ext(f.filename)] || Paperclip;
                    return (
                      <li
                        key={f.id}
                        data-testid={`evidence-panel-item-${f.id}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50 hover:bg-viva-50/40 border border-slate-100 group"
                      >
                        <Icon className="w-3.5 h-3.5 text-viva-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-slate-800 truncate" title={f.filename}>
                            {f.doc_name?.trim() || f.filename}
                          </div>
                          {f.doc_name?.trim() && (
                            <div className="text-[10px] text-slate-500 truncate" title={f.filename}>
                              {f.filename}
                            </div>
                          )}
                          <div className="text-[10px] text-slate-400">
                            {humanSize(f.size)} · {fmtTime(f.uploaded_at)}
                            {f.doc_date && <> · <span className="text-slate-500">doc {f.doc_date}</span></>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => download(f)}
                          className="p-1 rounded text-viva-700 opacity-0 group-hover:opacity-100 hover:bg-viva-100"
                          title="Scarica"
                          data-testid={`evidence-panel-download-${f.id}`}
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => remove(f)}
                            className="p-1 rounded text-red-600 opacity-0 group-hover:opacity-100 hover:bg-red-50"
                            title="Elimina"
                            data-testid={`evidence-panel-delete-${f.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
